import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

const isEmpty = (obj) =>
  [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;

const initialValue = (value) => {
  if (isEmpty(value)) {
    return null;
  }

  return value;
};

const LoadableSelect = ({
  label,
  id,
  value,
  onChange,
  onBlur,
  options,
  optionsErrorCallback,
  disabled = false,
  required = false,
  inline = false,
  searchable = false,
  dataTest,
  error,
  isClearable = false,
  isMulti = false,
  isLoading = false
}) => {
  const [selectedValue, setSelectedValue] = useState(initialValue(value));
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [isLoadingState, setIsLoadingState] = useState(isLoading ?? false);
  const field = label.toLowerCase();

  const handleChange = (value, { action }) => {
    if (['input-change', 'select-option', 'clear', 'remove-value'].includes(action)) {
      setSelectedValue(value);
      if (!isMulti) {
        onChange(value?.value ?? null);
      } else {
        onChange((value ?? []).map((valueItem) => valueItem?.value ?? null));
      }
    }
    return;
  };

  const getOptionsAsync = async () => {
    setIsLoadingState(true);
    try {
      const res = await options();
      setAsyncOptions(
        res?.map((tag) => ({ label: tag.label ?? tag[field], value: tag.value ?? tag[field] })) ??
          []
      );

      setIsLoadingState(false);
    } catch (err) {
      optionsErrorCallback(err);
      setIsLoadingState(false);
    }
  };

  useEffect(() => {
    if (value !== selectedValue && !isEmpty(value)) {
      const normalizeValueBeforeSelection = (value) => {
        if (['number', 'string'].includes(typeof value)) {
          return { label: value, value };
        }

        return value;
      };

      // I am of the opinion that we shouldn't attempt to align with an existing async option.
      // The options get loaded once the dropdown menu is accessed, and the selection by value might occur prior to this."
      // Don't -> asyncOptions.filter((option) => ...) ?? [])

      setSelectedValue(
        Array.isArray(value)
          ? value.map((valueItem) => normalizeValueBeforeSelection(valueItem))
          : normalizeValueBeforeSelection(value)
      );
    } else {
      setSelectedValue(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    setIsLoadingState(isLoading);
  }, [isLoading]);

  let wrapperClassName;
  let errorClassName;
  let labelClassName = 'block font-sans text-sm font-bold text-gray-600 mb-1';
  if (inline) {
    wrapperClassName = `flex flex-wrap mb-1 items-center justify-end w-full`;
    labelClassName = `${labelClassName} w-1/4 text-right pr-4`;
  } else {
    wrapperClassName = 'block mb-8';
  }

  if (error) {
    labelClassName = `${labelClassName} text-red-100`;
    errorClassName = 'block w-full w-3/4 font-medium tracking-wide text-red-500 text-xs mt-1 mb-2';
  } else {
    errorClassName = 'font-medium tracking-wide text-red-500 text-xs mt-1 ml-1';
  }

  return (
    <div className={wrapperClassName}>
      <label id={`${id}-label`} className={labelClassName}>
        {label}
        {required ? <span className="text-blue-100 my-">*</span> : null}
      </label>
      <div className="max-w-full w-3/4 items-start">
        <Select
          id={id}
          inputId={id}
          name={id}
          classNames={{
            container: () => 'grow focus:outline-none focus:border-purple-100',
            control: () =>
              'rounded-sm border-2 border-grey-200 bg-white shadow-sm px-4 py-1 hover:border-grey-300 outline-none ring-inset',
            valueContainer: () => 'font-sans text-base font-normal text-black/85 -my-0.5 -mx-2',
            input: () => 'focus:border-purple-100',
            dropdownIndicator: () => '-m-2'
          }}
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              borderWidth: '2px',
              boxShadow: 'none',
              borderColor: error ? '#E0091F' : '#C4CFDA',
              ':hover': {
                borderColor: '#43494F'
              },
              ':focus-within': {
                borderColor: '#552EEA'
              },
              ':disabled': {
                backgroundColor: '#F6F8FA',
                borderColor: '#C4CFDA',
                color: '#25292E'
              }
            }),
            menu: (baseStyles) => ({
              ...baseStyles,
              marginTop: '2px',
              borderRadius: '2px',
              boxShadow:
                '0px 9px 28px 8px rgba(0, 0, 0, 0.05), 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12)'
            }),
            indicatorSeparator: (baseStyles) => ({
              ...baseStyles,
              display: 'none'
            }),
            input: (baseStyles) => ({
              ...baseStyles,
              'input:focus': {
                boxShadow: 'none'
              }
            }),
            option: (baseStyles, { isSelected, isFocused }) => ({
              ...baseStyles,
              backgroundColor: isSelected ? '#552EEA' : isFocused ? '#F6F8FA' : '#FFFFFF'
            })
          }}
          value={selectedValue}
          onChange={handleChange}
          onBlur={onBlur}
          required={required}
          isDisabled={disabled}
          onMenuOpen={getOptionsAsync}
          options={asyncOptions}
          placeholder="Select"
          isSearchable={searchable}
          isLoading={isLoadingState}
          aria-labelledby={`${id}-label`}
          data-testid={dataTest}
          isClearable={isClearable}
          isMulti={isMulti}
        />
      </div>
      {error ? (
        <small data-testid={`${dataTest}-error`} className={errorClassName}>
          {error}
        </small>
      ) : null}
    </div>
  );
};

LoadableSelect.propTypes = {
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.shape({})
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  options: PropTypes.func.isRequired,
  optionsErrorCallback: PropTypes.func.isRequired,
  dataTest: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  inline: PropTypes.bool,
  searchable: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isClearable: PropTypes.bool,
  isMulti: PropTypes.bool,
  isLoading: PropTypes.bool
};

export default LoadableSelect;
